/**
 * PostHog del sitio público de Pizas — SOLO tras «Aceptar» en el aviso de cookies (paquete legal v2026-10-09).
 *
 * Antes el snippet estaba en public/index.html y cargaba en todas las rutas (admin incluido) con grabación de
 * sesión. Ahora:
 * - No se carga ningún script de PostHog hasta que hay consentimiento (lib/analyticsConsent.js).
 * - Nunca en el panel (/admin, /super) ni en la página de pago (/pagar/*): ni se inicializa ahí y,
 *   si ya estaba cargado, `before_send` descarta los eventos de esas rutas.
 * - Sin grabación de sesión, sin autocaptura (ni clics ni inputs), sin heatmaps ni encuestas.
 *   Solo $pageview / $pageleave.
 * - «Rechazar» después de aceptar: opt-out y se borran cookies/almacenamiento ph_*.
 * REACT_APP_POSTHOG_ENABLED=false en el build lo apaga por completo.
 * La clave es la clave pública de proyecto (phc_…), la misma que tenía index.html.
 */
import { CONSENT_GRANTED, hasAnalyticsConsent, onAnalyticsConsentChange } from './analyticsConsent';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

const EXCLUDED_PATH_RE = /^\/(admin|super|pagar)(\/|$)/i;

export function isAnalyticsExcludedPath(pathname) {
  return typeof pathname === 'string' && EXCLUDED_PATH_RE.test(pathname);
}

function enabled() {
  return process.env.REACT_APP_POSTHOG_ENABLED !== 'false' && Boolean(POSTHOG_KEY) && typeof window !== 'undefined';
}

function currentPathExcluded() {
  return isAnalyticsExcludedPath(window.location.pathname);
}

function urlExcluded(url) {
  if (typeof url !== 'string' || !url) return false;
  try {
    return isAnalyticsExcludedPath(new URL(url, window.location.origin).pathname);
  } catch (e) {
    return true;
  }
}

function stripQuery(url) {
  if (typeof url !== 'string' || !url) return url;
  try {
    const u = new URL(url, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch (e) {
    return url;
  }
}

/** Filtro de eventos: sin consentimiento, rutas excluidas o enlaces con token (/baja?t=…) no se envía nada. */
export function filterAnalyticsEvent(event) {
  if (!event) return null;
  if (!hasAnalyticsConsent()) return null;
  if (currentPathExcluded()) return null;
  const props = event.properties || {};
  if (urlExcluded(props.$current_url) || urlExcluded(props.$pathname)) return null;
  ['$current_url', '$referrer', '$initial_referrer', '$prev_pageview_url'].forEach((k) => {
    if (urlExcluded(props[k])) props[k] = '$direct';
    else if (props[k] && props[k] !== '$direct') props[k] = stripQuery(props[k]);
  });
  [props.$set_once, event.$set_once, props.$set, event.$set].forEach((obj) => {
    if (!obj) return;
    ['$initial_referrer', '$initial_current_url', '$initial_pathname', '$current_url'].forEach((k) => {
      if (urlExcluded(obj[k])) delete obj[k];
      else if (obj[k]) obj[k] = stripQuery(obj[k]);
    });
  });
  return event;
}

/** Stub oficial de PostHog (cola de llamadas) + carga de array.js; solo se ejecuta tras el consentimiento. */
function loadSnippet() {
  if (window.posthog && window.posthog.__SV) return window.posthog;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o = 'init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags on onFeatureFlags onSessionId identify setPersonProperties group resetGroups reset get_distinct_id getGroups get_session_id alias set_config get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' '),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
  return window.posthog;
}

let initialized = false;
let listenerInstalled = false;

const isPostHogKey = (k, keepOptOut) => /^(ph_|__ph)/.test(k) && !(keepOptOut && /^__ph_opt_in_out_/.test(k));

function clearPostHogStorage(keepOptOut) {
  try {
    [window.localStorage, window.sessionStorage].forEach((store) => {
      if (!store) return;
      Object.keys(store)
        .filter((k) => isPostHogKey(k, keepOptOut))
        .forEach((k) => store.removeItem(k));
    });
  } catch (e) {
    /* almacenamiento no disponible */
  }
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (isPostHogKey(name, keepOptOut)) {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname.split('.').slice(-2).join('.')}`;
      }
    });
  } catch (e) {
    /* sin cookies */
  }
}

function stopPostHog() {
  if (initialized && window.posthog) {
    try {
      window.posthog.opt_out_capturing();
      window.posthog.set_config({ disable_persistence: true });
    } catch (e) {
      /* nada */
    }
  }
  clearPostHogStorage(initialized);
}

function startPostHog() {
  if (!enabled() || !hasAnalyticsConsent() || currentPathExcluded()) return;
  if (initialized) {
    try {
      window.posthog.set_config({ disable_persistence: false });
      window.posthog.opt_in_capturing();
    } catch (e) {
      /* nada */
    }
    return;
  }
  const ph = loadSnippet();
  ph.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    enable_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    disable_surveys: true,
    disable_session_recording: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    persistence: 'localStorage+cookie',
    cookie_expiration: 365,
    before_send: filterAnalyticsEvent,
  });
  initialized = true;
  try {
    if (window.posthog.has_opted_out_capturing && window.posthog.has_opted_out_capturing()) {
      window.posthog.opt_in_capturing();
    }
  } catch (e) {
    /* el stub aún no tiene el método: la cola lo aplicará */
  }
}

/** Se llama desde el aviso de cookies (páginas públicas). Arranca solo si ya hay consentimiento. */
export function ensurePostHogStarted() {
  if (!enabled()) return;
  if (!listenerInstalled) {
    listenerInstalled = true;
    onAnalyticsConsentChange((value) => (value === CONSENT_GRANTED ? startPostHog() : stopPostHog()));
  }
  startPostHog();
}

export function isPostHogLoaded() {
  return initialized;
}
